// Rewrites outgoing API paths to their asset-class-scoped form, driven entirely by the scoped-route
// catalog returned by the backend in user/info (no hardcoded scoped/cross list — see assetClassState).
//
// Each catalog entry is a canonical template such as `/api/{assetClass}/detail/document/{documentId}`.
// The `{assetClass}` segment marks where the selected slug is injected; other `{param}` segments are
// single-segment wildcards. A path matches when, after removing the {assetClass} slot, its segments
// line up with the template (literals equal, params wildcard). Cross paths match no template and pass
// through untouched. The transform is idempotent: an already-scoped path has one extra segment and
// will not match again.

import { getSelectedAssetClass, getScopedRoutes } from './assetClassState'

const ASSET_CLASS_TOKEN = '{assetClass}'

// One warning per distinct path per page session (not per call) — several unscoped paths are hit
// repeatedly during normal use (e.g. /api/user/info at every boot); without this the log would
// drown the one signal it exists for. Resets on reload, which is fine: the goal is "does this path
// shape still fall through at all", not counting occurrences.
const _warnedPaths = new Set()

function pathSegments(path) {
  return path.split('/').filter((segment) => segment.length > 0)
}

function isParam(segment) {
  return segment.startsWith('{') && segment.endsWith('}')
}

export function scopeUrl(url) {
  const slug = getSelectedAssetClass()
  if (!slug || typeof url !== 'string' || !url.startsWith('/')) {
    return url
  }

  const routes = getScopedRoutes()
  if (!routes || routes.length === 0) {
    return url
  }

  const splitIndex = url.search(/[?#]/)
  const path = splitIndex === -1 ? url : url.slice(0, splitIndex)
  const suffix = splitIndex === -1 ? '' : url.slice(splitIndex)
  const urlSegments = pathSegments(path)

  for (const template of routes) {
    const templateSegments = pathSegments(template)
    const assetClassIndex = templateSegments.indexOf(ASSET_CLASS_TOKEN)
    if (assetClassIndex === -1) {
      continue
    }
    // The outgoing (legacy) path has exactly one fewer segment than the canonical template.
    if (urlSegments.length !== templateSegments.length - 1) {
      continue
    }

    let matched = true
    let urlIndex = 0
    for (let templateIndex = 0; templateIndex < templateSegments.length; templateIndex++) {
      if (templateIndex === assetClassIndex) {
        continue
      }
      const templateSegment = templateSegments[templateIndex]
      if (!isParam(templateSegment) && templateSegment !== urlSegments[urlIndex]) {
        matched = false
        break
      }
      urlIndex++
    }
    if (!matched) {
      continue
    }

    const scopedSegments = urlSegments.slice()
    scopedSegments.splice(assetClassIndex, 0, slug)
    return '/' + scopedSegments.join('/') + suffix
  }

  // Reached with a valid slug and a non-empty catalog, yet no template matched: either this is a
  // genuinely cross-asset-class call (correct — the catalog only ever contains {assetClass}
  // templates, so a cross path can never match one) or a scoped-looking call whose literal has
  // drifted from the backend's route shape (a real bug, currently indistinguishable from the
  // first case here). Logged, not thrown: the catalog deliberately carries no hardcoded cross/
  // scoped list (see the module comment), so this function alone cannot tell which case it is —
  // a human reviewing this log against the known scoped feature categories can. The eventual goal
  // is to run with an empty log for a monitoring period, which is the evidence needed to retire
  // the legacy (unscoped) backend routes with confidence instead of guessing.
  if (!_warnedPaths.has(path)) {
    _warnedPaths.add(path)
    console.warn(`[assetClassScope] no scoped-route template matched "${path}" — sent unscoped.`)
  }
  return url
}
